/**
 * "Applicant Consent" accordion on the combined report (client 2026-09-08):
 * one row per member with the consent form's own timestamp on the right,
 * expanding to the SAME consent rows the individual page's Applicant Consent
 * card shows (ApplicantConsentBody — Terms of Service, Credit Check
 * Authorization, and Landlord Terms & Conditions). The demo has no uploaded
 * org PDF, so the same blank landlord-terms file the individual card uses
 * stands in. Every member consented to apply, so every row expands.
 */
import type {
	GroupMember,
	MemberConsent,
} from '#app/utils/application-detail-v2/types'
import { useFormatDate } from '#app/utils/application-detail-v2/vendor/use-format-date'
import { IconSquare } from '../primitives'
import { ApplicantConsentBody } from '../sections/ApplicantConsent'
import { MemberAccordion } from './MemberAccordion'

export function MemberConsents({
	members,
	memberConsents,
}: {
	members: GroupMember[]
	memberConsents: Record<string, MemberConsent>
}) {
	// Same "21 July, 2026, 04:00 PM" format as the individual card.
	const { formatPipelineDateTime } = useFormatDate()
	return (
		<MemberAccordion
			title="Applicant Consent"
			icon={
				<IconSquare tone="mint">
					<img
						src="/img/applicant-consent-icon.svg"
						alt=""
						aria-hidden="true"
						className="size-5"
					/>
				</IconSquare>
			}
			members={members}
			row={(member) => {
				const consent = memberConsents[member.applicationId] ?? {
					consentedAt: null,
					orgTerms: null,
				}
				const when = consent.consentedAt
					? formatPipelineDateTime(consent.consentedAt)
					: null
				return {
					expandable: true,
					summary: (
						<span className="text-sm text-[#747474]">
							{when && when !== '-' ? when : 'Consented'}
						</span>
					),
					body: (
						<ApplicantConsentBody
							orgTerms={consent.orgTerms}
							fallbackTermsHref="/demo-landlord-terms.pdf"
							className="pb-4"
						/>
					),
				}
			}}
		/>
	)
}
